// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Grid, IconButton, Link, TableCell, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import { Link as RouterLink } from 'react-router-dom';
import api from '../../utils/api';
import { dateTime, dateUtc } from '../../utils/dates';
import { locationText } from '../../utils/recherche';
import SiteServerDetail from './DetailServeurSite';
import { UpdateFailure, SecretField, StepIcon } from './commun';

/**
 * Ligne d'un serveur dans la liste : état, client, version, puis, dépliés, le
 * détail de l'installation, les caméras et les gestes de libération et de
 * suppression.
 */

const STATUS_COLORS = {
  prepared: 'default',
  claimed: 'warning',
  active: 'success',
  retired: 'error',
};

// « Retiré » n'a pas d'onglet dans la liste (un serveur supprimé l'est
// réellement), mais reste lisible sur une ligne qui porte ce statut.
const STATUS_LABELS = {
  prepared: 'Préparé',
  claimed: 'Activé',
  active: 'En service',
  retired: 'Retiré',
};

const STEP_ORDER = ['network', 'backend', 'ha_config', 'camera', 'tunnel'];
const STEP_LABELS = {
  network: 'Réseau',
  backend: 'Connexion serveur',
  ha_config: 'Configuration box',
  camera: 'Caméra',
  tunnel: 'Tunnel sécurisé',
};

// Un serveur d'établissement (kit rattaché à un site) remonte sa santé toutes
// les cinq minutes, une box toutes les minutes : la fenêtre de deux minutes le
// montrerait hors ligne trois minutes sur cinq. Il prend celle du backend
// (detector_health._KIT_ONLINE_WINDOW, dix minutes, comme BlocServeurs.jsx).
function isBoxOnline(server) {
  // Valeur du backend quand il la rend (serveur d'établissement) : la même que
  // sur la fiche de l'établissement.
  if (typeof server?.detector?.box_online === 'boolean') return server.detector.box_online;
  const d = dateUtc(server.last_seen);
  const windowMinutes = server.site_id ? 10 : 2;
  return !!d && Date.now() - d.getTime() < windowMinutes * 60 * 1000;
}

function formatDate(iso) {
  const d = dateUtc(iso);
  return d ? d.toLocaleDateString('fr-FR') : '—';
}

// Version en service. Pour un serveur d'établissement, jamais `agent_version` :
// c'est le protocole de /hello (`site-poseur/1`), pas une version du produit.
// Sans version appliquée, on lit le plancher anti-rejeu : relevé par le canal,
// il porte la dernière version tenue ; écrit par la clé, il vaut 0.0.0, que le
// backend rend null avec la date de préparation de la charge.
function serverVersion(server) {
  if (!server.site_id) return server.agent_version || null;
  const update = server.mise_a_jour || {};
  if (update.version_en_service) return update.version_en_service;
  if (update.version_posee) return `${update.version_posee} (dernière tenue)`;
  if (update.charge_preparee_le) return `jamais mis à jour (charge du ${formatDate(update.charge_preparee_le)})`;
  return null;
}

// Ajout d'une caméra. Le mot de passe du hotspot d'installation des caméras
// (Vigeos-CAM-XXXX) est commun à la flotte et servi par le backend, serveurs
// d'établissement compris, sauf avec VIGEOS_PAIRING_PSK_SERVI=0. L'écran
// l'affiche dès que la réponse de `POST /kits/{id}/cameras` porte un
// `hotspot_psk` non vide, et signale sinon qu'il n'est pas servi. Pas de drapeau
// de build : il divergerait du backend.
const EMPTY_CAMERA_FORM = { mac: '', serial: '', firmware: '' };

export default function ServerRow({ serveur: server, onCopy, onToast, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [cameras, setCameras] = useState(null);
  const [camerasLoading, setCamerasLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // Dialogue « ajouter une caméra » (chemin secondaire : banc, SAV, pré-affectation).
  const [camOpen, setCamOpen] = useState(false);
  const [camForm, setCamForm] = useState(EMPTY_CAMERA_FORM);
  const [camSaving, setCamSaving] = useState(false);
  const [camCreated, setCamCreated] = useState(null);
  const [camError, setCamError] = useState('');

  const online = isBoxOnline(server);
  const steps = (server.install_state && server.install_state.steps) || {};
  const details = (server.install_state && server.install_state.details) || {};
  const facility = server.site_id;
  // Box de foyer qui porte encore un client ou un logement : le backend
  // refuse sa suppression (409) tant qu'elle n'est pas libérée. On propose
  // « Libérer » et on ne montre pas un « Supprimer » voué au refus.
  const householdInService = !facility && !!(server.client_id || server.property_id);

  const loadCameras = async () => {
    setCamerasLoading(true);
    try {
      const res = await api.get(`/kits/${server.id}/cameras`);
      setCameras(res.data.cameras || []);
    } catch {
      onToast('Impossible de charger les caméras du serveur');
    } finally {
      setCamerasLoading(false);
    }
  };

  useEffect(() => {
    if (expanded && cameras === null) loadCameras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const handleRelease = async () => {
    // La libération invalide le secret du kit : le QR imprimé ne réactive plus
    // rien. Une box repasse par l'atelier (nouveau secret, nouvelle étiquette),
    // un serveur d'établissement demande une nouvelle clé de pose.
    const suite = facility
      ? 'il faudra graver une nouvelle clé de pose pour le remettre en service.'
      : 'la box devra repasser par l’atelier (nouveau secret, nouvelle étiquette) avant toute réactivation.';
    if (!window.confirm(
      `Libérer ${server.code} ?\n\nIl sera détaché de son client, le logement lié sera désactivé `
      + `et son secret sera invalidé : le QR actuel ne servira plus. Ensuite, ${suite}`
    )) return;
    setBusy(true);
    try {
      const res = await api.post(`/kits/${server.id}/release`);
      onToast(`${server.code} libéré. ${res.data?.prochaine_etape || `Ensuite, ${suite}`}`);
      onChanged();
    } catch (e) {
      onToast(e.response?.data?.detail || 'Échec de la libération du serveur');
    } finally {
      setBusy(false);
    }
  };

  // Suppression définitive, pas de passage à « Retiré ». La trace (code,
  // séries, établissement) reste au journal d'audit du backend.
  const handleDelete = async () => {
    if (!window.confirm(
      `Supprimer définitivement le serveur ${server.code} ?\n\n`
      + `Son identité, ses caméras enregistrées, ses commandes en attente et ses ordres de clé `
      + `disparaissent. Une clé déjà gravée avec cette identité ne posera plus rien, et une machine `
      + `qui l'utilise encore ne pourra plus se connecter. Une trace reste au journal d'audit.`
    )) return;
    setBusy(true);
    try {
      await api.delete(`/kits/${server.id}`);
      onToast(`Serveur ${server.code} supprimé.`);
      onChanged();
    } catch (e) {
      const detail = e.response?.data?.detail;
      onToast(typeof detail === 'string' && detail
        ? detail
        : `Échec de la suppression du serveur${e.response ? ` (HTTP ${e.response.status})` : ''}`);
    } finally {
      setBusy(false);
    }
  };

  const handleCamSubmit = async () => {
    if (!camForm.mac.trim()) { setCamError('L’adresse MAC est requise.'); return; }
    setCamSaving(true);
    setCamError('');
    try {
      const payload = { mac: camForm.mac.trim() };
      if (camForm.serial.trim()) payload.serial = camForm.serial.trim();
      if (camForm.firmware.trim()) payload.firmware = camForm.firmware.trim();
      const res = await api.post(`/kits/${server.id}/cameras`, payload);
      setCamCreated(res.data);
      loadCameras();
    } catch (e) {
      setCamError(e.response?.data?.detail || 'Échec de l’enregistrement de la caméra');
    } finally {
      setCamSaving(false);
    }
  };

  const closeCamDialog = () => {
    if (camSaving) return;
    const created = !!camCreated;
    setCamOpen(false);
    setCamForm(EMPTY_CAMERA_FORM);
    setCamCreated(null);
    setCamError('');
    if (created) onChanged(); // rafraîchit cameras_count dans la liste
  };

  const camerasCount = server.cameras_count ?? (cameras ? cameras.length : null);
  const lieu = locationText(server.postal_code, server.city);

  return (
    <>
      <TableRow hover>
        <TableCell>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{server.code}</Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={STATUS_LABELS[server.status] || server.status}
            color={STATUS_COLORS[server.status] || 'default'}
            size="small"
          />
          {/* Secret invalidé (libération ou réinitialisation) : le QR ne
              réactive plus rien tant que le kit n'est pas re-préparé. */}
          {server.a_repreparer && server.status !== 'retired' && (
            <Tooltip title={facility
              ? 'Secret invalidé : graver une nouvelle clé de pose pour ce serveur'
              : 'Secret invalidé : re-préparer la box à l’atelier (nouvelle étiquette)'}>
              <Chip label="À re-préparer" color="warning" variant="outlined" size="small" sx={{ ml: 0.5 }} />
            </Tooltip>
          )}
        </TableCell>
        <TableCell>
          <Tooltip title={server.last_seen ? `Dernier contact : ${dateTime(server.last_seen, '—')}` : 'Serveur jamais vu en ligne'}>
            <Box sx={{
              width: 10, height: 10, borderRadius: '50%', display: 'inline-block',
              // Pas `grey.400` : la palette sombre de MUI le rend en #bdbdbd, et
              // la pastille hors ligne brillerait plus que celle en ligne.
              // `text.disabled`, gris-bleu de la charte, se voit mais reste éteint.
              bgcolor: online ? 'success.main' : 'text.disabled',
            }} />
          </Tooltip>
        </TableCell>
        <TableCell>
          {/* Un serveur d'établissement mène à la fiche de son client, un kit de
              foyer à celle du sien : deux natures de client, deux routes. */}
          {facility ? (
            <>
              {server.organisation_id ? (
                <Link component={RouterLink} to={`/clients/etablissement/${server.organisation_id}`}
                  underline="hover" variant="body2">
                  {server.organisation_name || 'Voir le client'}
                </Link>
              ) : (
                <Typography variant="body2">{server.organisation_name || '—'}</Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block">
                {server.site_name || 'Établissement inconnu'}
              </Typography>
            </>
          ) : server.client_id ? (
            <>
              <Link component={RouterLink} to={`/clients/${server.client_id}`} underline="hover" variant="body2">
                {server.client_name || 'Voir le client'}
              </Link>
              <Typography variant="caption" color="text.secondary" display="block">
                {server.property_name || 'Foyer'}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">Non affecté</Typography>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{lieu || '—'}</Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2">{camerasCount === null ? '—' : camerasCount}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{server.hardware_model || '—'}</Typography>
        </TableCell>
        <TableCell>
          {/* Visible sans ouvrir le détail : quelle version tourne, et si la
              dernière mise à jour a échoué (motif au survol). */}
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
            {serverVersion(server) || 'inconnue'}
          </Typography>
          <UpdateFailure echec={server.mise_a_jour?.echec} />
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{formatDate(server.created_at)}</Typography>
        </TableCell>
        <TableCell align="right">
          <Tooltip title={expanded ? 'Masquer le détail' : 'Voir le détail'}>
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={10} sx={{ py: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            {/* Panneau creusé dans la ligne : voile sombre sur le verre de la
                carte, bordé du filet de la charte qui marque où commence et où
                finit le détail. */}
            <Box sx={{
              py: 2, px: 1, borderRadius: 1,
              bgcolor: 'rgba(4,9,20,.35)', border: 1, borderColor: 'divider',
            }}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    {facility ? 'Établissement' : 'Logement'}
                  </Typography>
                  <Typography variant="body2">
                    {facility ? (server.site_name || '—') : (server.property_name || '—')}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    {facility ? 'Version en service' : 'Version agent'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {serverVersion(server) || 'inconnue'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">Dernier contact</Typography>
                  <Typography variant="body2">{dateTime(server.last_seen, '—')}</Typography>
                </Grid>
                {(server.address || lieu) && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Adresse</Typography>
                    <Typography variant="body2">
                      {[server.address, lieu].filter(Boolean).join(', ')}
                    </Typography>
                  </Grid>
                )}
                {server.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Notes</Typography>
                    <Typography variant="body2">{server.notes}</Typography>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {facility ? 'État du serveur' : 'Installation'}
              </Typography>
              {facility ? (
                <SiteServerDetail serveur={server} steps={steps} details={details} />
              ) : (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {STEP_ORDER.map(step => (
                    <Tooltip key={step} title={details[step] || ''}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <StepIcon status={steps[step] || 'pending'} />
                        <Typography variant="caption" color={steps[step] === 'error' ? 'error' : 'text.secondary'}>
                          {STEP_LABELS[step]}
                        </Typography>
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
              )}

              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2">Caméras du serveur</Typography>
                <Button
                  size="small" variant="outlined" startIcon={<VideocamIcon />}
                  onClick={() => { setCamOpen(true); setCamForm(EMPTY_CAMERA_FORM); setCamCreated(null); setCamError(''); }}
                >
                  Ajouter une caméra
                </Button>
              </Box>
              {camerasLoading ? (
                <CircularProgress size={20} />
              ) : !cameras || cameras.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Aucune caméra enregistrée sur ce serveur.</Typography>
              ) : (
                cameras.map(cam => (
                  <Box key={cam.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      label={cam.status === 'online' ? 'En ligne' : cam.status === 'retired' ? 'Retirée' : 'Hors ligne'}
                      color={cam.status === 'online' ? 'success' : cam.status === 'retired' ? 'error' : 'default'}
                      size="small"
                    />
                    <Typography variant="body2">{cam.label || cam.hotspot_ssid || 'Caméra'}</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>
                      {cam.mac}
                    </Typography>
                    {cam.last_seen && (
                      <Typography variant="caption" color="text.secondary">
                        vue le {dateTime(cam.last_seen, '—')}
                      </Typography>
                    )}
                  </Box>
                ))
              )}

              {server.status !== 'retired' && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(((server.status === 'claimed' || server.status === 'active') && !facility) || householdInService) && (
                      <Tooltip title="Détache la box de sa famille et invalide son secret : elle repasse par l’atelier (nouvelle étiquette) avant toute réactivation">
                        <span>
                          <Button size="small" variant="outlined" color="warning" startIcon={<LinkOffIcon />} onClick={handleRelease} disabled={busy}>
                            Libérer
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    {householdInService ? (
                      <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                        Box de foyer rattachée à un client : la libérer avant de pouvoir la supprimer.
                      </Typography>
                    ) : (
                      <Tooltip title="Matériel perdu, défectueux ou préparé pour rien : le serveur disparaît, son identité ne pourra plus rien activer">
                        <span>
                          <Button size="small" variant="outlined" color="error" startIcon={<DeleteForeverIcon />} onClick={handleDelete} disabled={busy}>
                            Supprimer
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Ajout d'une caméra : identifiants affichés une seule fois, à la création. */}
      <Dialog open={camOpen} onClose={closeCamDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{camCreated ? 'Caméra enregistrée' : `Ajouter une caméra au serveur ${server.code}`}</DialogTitle>
        <DialogContent>
          {camError && <Alert severity="error" sx={{ mb: 2 }}>{camError}</Alert>}
          {camCreated ? (
            <>
              {/* Aucune étape atelier : l'image du lot est identique pour toutes
                  les caméras. Le mot de passe RTSP est généré côté serveur et posé
                  par la box à l'appairage ; il n'est pas renvoyé ici
                  (docs/cameras/CAMERA_PAIRING.md). */}
              <Alert severity="info" sx={{ mb: 2 }}>
                Rien à graver : le mot de passe RTSP est posé par la box à l&rsquo;appairage.
                Le hotspot d&rsquo;installation est <strong>commun à toutes les caméras Vigeos</strong>
                {' '}(mot de passe gravé dans l&rsquo;image du lot) ; seul le SSID dépend de la MAC.
              </Alert>
              <Grid container spacing={1.5}>
                <SecretField label="SSID hotspot (dérivé de la MAC)" value={camCreated.hotspot_ssid} onCopy={onCopy} />
                {camCreated.hotspot_psk ? (
                  <SecretField label="Mot de passe hotspot (commun à la flotte)" value={camCreated.hotspot_psk} onCopy={onCopy} />
                ) : (
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      Mot de passe du hotspot non servi : le backend a coupé le drapeau
                      VIGEOS_PAIRING_PSK_SERVI (vrai par défaut). Le mot de passe reste commun
                      à la flotte et gravé dans l&rsquo;image du lot ; pour l&rsquo;afficher ici,
                      retirer VIGEOS_PAIRING_PSK_SERVI=0 de l&rsquo;environnement du backend.
                    </Alert>
                  </Grid>
                )}
                <SecretField label="MAC" value={camCreated.mac} onCopy={onCopy} />
              </Grid>
            </>
          ) : (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Alert severity="info">
                  Chemin secondaire (banc, SAV, pré-affectation d&rsquo;un lot) : en usage normal
                  c&rsquo;est l&rsquo;appairage sur place qui déclare la caméra.
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth size="small" label="Adresse MAC *" placeholder="aa:bb:cc:dd:ee:ff"
                  value={camForm.mac} onChange={e => setCamForm(f => ({ ...f, mac: e.target.value }))}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small" label="Numéro de série"
                  value={camForm.serial} onChange={e => setCamForm(f => ({ ...f, serial: e.target.value }))}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small" label="Firmware"
                  value={camForm.firmware} onChange={e => setCamForm(f => ({ ...f, firmware: e.target.value }))}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {camCreated ? (
            <Button variant="contained" onClick={closeCamDialog}>Fermer</Button>
          ) : (
            <>
              <Button onClick={closeCamDialog} disabled={camSaving}>Annuler</Button>
              <Button variant="contained" onClick={handleCamSubmit} disabled={camSaving}>
                {camSaving ? <CircularProgress size={18} /> : 'Enregistrer'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
